<template>
  <ContentWrap title="经营类目">
    <template #header>
      <el-button type="primary" @click="openApplyDialog">
        <Icon icon="ep:plus" class="mr-5px" />申请新类目
      </el-button>
    </template>

    <!-- 已通过的类目 -->
    <el-table v-loading="loading" :data="list" stripe class="mb-16px">
      <el-table-column label="类目名称" min-width="200" prop="categoryName" />
      <el-table-column label="上级类目" min-width="150" prop="parentCategoryName">
        <template #default="{ row }">
          {{ row.parentCategoryName || '-' }}
        </template>
      </el-table-column>
      <el-table-column label="平台佣金率" width="120" prop="defaultCommissionRate">
        <template #default="{ row }">
          {{ row.defaultCommissionRate != null ? row.defaultCommissionRate + '%' : '-' }}
        </template>
      </el-table-column>
      <el-table-column label="店铺佣金率" width="120" prop="commissionRate">
        <template #default="{ row }">
          {{ row.commissionRate != null ? row.commissionRate + '%' : '-' }}
        </template>
      </el-table-column>
    </el-table>

    <!-- 申请记录 -->
    <div v-if="applyList.length > 0" class="mt-24px">
      <h4 class="mb-12px">申请记录</h4>
      <el-table :data="applyList" stripe size="small">
        <el-table-column label="类目名称" min-width="150" prop="categoryName" />
        <el-table-column label="上级类目" width="120" prop="parentCategoryName">
          <template #default="{ row }">
            {{ row.parentCategoryName || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template #default="{ row }">
            <el-tag v-if="row.applyStatus === 0" type="warning">待审核</el-tag>
            <el-tag v-else-if="row.applyStatus === 1" type="success">已通过</el-tag>
            <el-tag v-else-if="row.applyStatus === 2" type="danger">已驳回</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="驳回原因" min-width="150" prop="rejectReason">
          <template #default="{ row }">
            {{ row.rejectReason || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="申请时间" width="180" prop="createTime" :formatter="dateFormatter" />
      </el-table>
    </div>

    <el-empty v-if="!loading && list.length === 0" description="暂无经营类目，请点击「申请新类目」添加" />
  </ContentWrap>

  <!-- 申请新类目弹窗 -->
  <el-dialog v-model="dialogVisible" title="申请经营类目" width="640px" :close-on-click-modal="false">
    <el-form label-width="80px">
      <el-form-item label="选择类目">
        <el-cascader
          v-model="selectedCategories"
          :options="platformCategories"
          :props="cascaderProps"
          class="w-full"
          placeholder="请选择要经营的类目（可多选）"
          filterable
          clearable
          collapse-tags
          collapse-tags-tooltip
          :max-collapse-tags="3"
          @change="handleCascaderChange"
        />
      </el-form-item>
      <!-- 已选类目佣金率预览 -->
      <el-form-item v-if="selectedDetails.length > 0" label="佣金率预览">
        <div class="commission-preview">
          <el-tag
            v-for="item in selectedDetails"
            :key="item.categoryId"
            class="mr-8px mb-8px"
            type="info"
          >
            {{ item.fullPath }} —
            <span class="font-bold">{{ item.commissionRate != null ? item.commissionRate + '%' : '未设置' }}</span>
          </el-tag>
        </div>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" :loading="submitting" :disabled="selectedCategories.length === 0" @click="handleSubmitApply">
        提交申请（{{ selectedCategories.length }} 个类目）
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ElMessage } from 'element-plus'
import { ContentWrap } from '@/components/ContentWrap'
import { dateFormatter } from '@/utils/formatTime'
import * as MerchantShopApi from '@/api/merchant/shop'
import * as CategoryApplyApi from '@/api/merchant/category-apply'

defineOptions({ name: 'MerchantShopCategory' })

const loading = ref(false)
const submitting = ref(false)
const list = ref([])
const applyList = ref([])

// 弹窗相关
const dialogVisible = ref(false)
const selectedCategories = ref<any[][]>([])
const platformCategories = ref<any[]>([])
const selectedDetails = ref<Array<{ categoryId: number; fullPath: string; commissionRate: number | null }>>([])

const cascaderProps = {
  value: 'id',
  label: 'name',
  children: 'children',
  checkStrictly: true,
  emitPath: true,
  multiple: true,  // 支持多选
}

// 加载已通过的类目
const getList = async () => {
  loading.value = true
  try {
    const data = await MerchantShopApi.getShopCategories()
    list.value = data || []
  } finally {
    loading.value = false
  }
}

// 加载申请记录
const getApplyHistory = async () => {
  try {
    const data = await CategoryApplyApi.getApplyList()
    applyList.value = data || []
  } catch {
    applyList.value = []
  }
}

// 打开申请弹窗，加载平台分类
const openApplyDialog = async () => {
  dialogVisible.value = true
  selectedCategories.value = []
  selectedDetails.value = []
  try {
    const data = await CategoryApplyApi.getPlatformCategoryTree()
    platformCategories.value = data || []
  } catch {
    ElMessage.error('加载平台分类失败')
    platformCategories.value = []
  }
}

// 级联选择器变化时，更新佣金率预览
const handleCascaderChange = (paths: any[][]) => {
  if (!paths || paths.length === 0) {
    selectedDetails.value = []
    return
  }
  selectedDetails.value = paths.map((path) => {
    const categoryId = path[path.length - 1]
    // 从树中查找节点获取佣金率和完整路径
    let nodes = platformCategories.value
    const pathNames: string[] = []
    let commissionRate: number | null = null
    for (const id of path) {
      const node = nodes.find((n: any) => n.id === id)
      if (node) {
        pathNames.push(node.name)
        commissionRate = node.defaultCommissionRate ?? null
        nodes = node.children || []
      }
    }
    return {
      categoryId,
      fullPath: pathNames.join(' / '),
      commissionRate,
    }
  })
}

// 从分类树中按路径查找节点名称
const findNodeName = (path: number[], nodes: any[]): string => {
  let currentNodes = nodes
  let name = ''
  for (let i = 0; i < path.length; i++) {
    const node = (currentNodes as any[]).find((n: any) => n.id === path[i])
    if (node) {
      name = node.name
      currentNodes = node.children || []
    }
  }
  return name
}

// 获取父级类目名称
const findParentName = (path: number[], nodes: any[]): string => {
  if (path.length <= 1) return ''
  let currentNodes = nodes
  let name = ''
  for (let i = 0; i < path.length - 1; i++) {
    const node = (currentNodes as any[]).find((n: any) => n.id === path[i])
    if (node) {
      name = node.name
      currentNodes = node.children || []
    }
  }
  return name
}

// 提交申请（批量）
const handleSubmitApply = async () => {
  if (!selectedCategories.value || selectedCategories.value.length === 0) {
    ElMessage.warning('请选择要经营的类目')
    return
  }
  // 构建批量申请数据
  const items = selectedCategories.value.map((path: number[]) => ({
    categoryId: path[path.length - 1],
    categoryName: findNodeName(path, platformCategories.value),
    parentCategoryName: findParentName(path, platformCategories.value),
  }))

  submitting.value = true
  try {
    await CategoryApplyApi.submitApply({ items })
    ElMessage.success(`${items.length} 个类目申请已提交，请等待审核`)
    dialogVisible.value = false
    await getApplyHistory()
  } catch (e: any) {
    // axios interceptor 已处理错误提示
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  getList()
  getApplyHistory()
})
</script>

<style scoped>
.commission-preview {
  display: flex;
  flex-wrap: wrap;
}
</style>
